<script setup>
import { computed, onMounted, onUnmounted, ref, toRef, watch } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import Modal from 'dashboard/components/Modal.vue';
import WahaAPI from 'dashboard/api/waha';
import InboxesAPI from 'dashboard/api/inboxes';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');

const toDataUrl = async value => {
  if (!value) return null;
  const trimmed = value.trim();

  if (!trimmed) return null;

  if (trimmed.startsWith('data:image')) {
    return trimmed;
  }

  const compact = trimmed.replace(/\s+/g, '');

  const isBase64 = () => {
    try {
      return btoa(atob(compact)) === compact;
    } catch (error) {
      return false;
    }
  };

  if (isBase64()) {
    return `data:image/png;base64,${compact}`;
  }

  return QRCode.toDataURL(trimmed);
};

const normalizeQrPayload = async payload => {
  if (!payload) return null;

  if (typeof payload === 'string') {
    return toDataUrl(payload);
  }

  const candidate =
    payload.base64 ||
    payload.qrcode?.base64 ||
    payload.qrcode?.code ||
    payload.qrcode ||
    payload.code ||
    payload.value ||
    null;

  return toDataUrl(candidate);
};

const inbox = toRef(props, 'inbox');
const { t } = useI18n();

const isGeneratingQR = ref(false);
const isSessionAction = ref(false);
const isRecreating = ref(false);
const isUpdatingFilters = ref(false);
const connectionStatus = ref(null);
const qrCode = ref(null);
const showQrModal = ref(false);

// Message filter settings
const filterSettings = ref({
  ignoreGroups: false,
  ignoreChannels: false,
  ignoreStatus: false,
  ignoreBroadcast: false,
});

const providerConfig = computed(() => inbox.value.provider_config || {});

const sessionName = computed(() => {
  const config = providerConfig.value;
  return config.session_name || config.instance_name || '';
});

const connectedStatuses = new Set(['open', 'connected', 'working']);

const canShowQRCode = computed(() => {
  return !connectedStatuses.has(connectionStatus.value || '');
});

const isConnected = computed(() =>
  connectedStatuses.has(connectionStatus.value || '')
);

const actionButtonLabel = computed(() => {
  return isConnected.value
    ? t('INBOX_MGMT.ADD.WAHA.DISCONNECT.BUTTON')
    : t('INBOX_MGMT.ADD.WAHA.START.BUTTON');
});

const primaryActionClasses = computed(() =>
  isConnected.value
    ? 'bg-red-500 hover:bg-red-600 text-white'
    : 'bg-woot-500 hover:bg-woot-600 text-white'
);

const connectionStatusLabel = computed(() => {
  const status = (connectionStatus.value || '').toLowerCase();

  switch (status) {
    case 'working':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.WORKING');
    case 'connected':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.CONNECTED');
    case 'open':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.OPEN');
    case 'close':
    case 'closed':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.CLOSE');
    case 'stopped':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.STOPPED');
    case 'starting':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.STARTING');
    case 'failed':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.FAILED');
    case 'connecting':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.CONNECTING');
    case 'scan_qr_code':
    case 'scan-qr-code':
    case 'scan qr code':
      return t('INBOX_MGMT.ADD.WAHA.STATUS.SCAN_QR_CODE');
    default:
      break;
  }

  if (!status) {
    return t('INBOX_MGMT.ADD.WAHA.STATUS.UNKNOWN');
  }

  return status
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
});

const isStatusOpen = status => {
  return connectedStatuses.has((status || '').toLowerCase());
};

const qrScanStatuses = new Set([
  'scan_qr_code',
  'scan-qr-code',
  'scan qr code',
]);

const loadConnectionStatus = async () => {
  try {
    if (!accountId.value || !inbox.value?.id) {
      connectionStatus.value = null;
      return;
    }

    const data = await WahaAPI.fetchStatus(accountId.value, inbox.value.id);
    const session = data?.session;
    const rawStatus =
      data?.status ||
      session?.status ||
      session?.state ||
      session?.connectionStatus ||
      '';

    if (session || rawStatus) {
      connectionStatus.value = rawStatus.toString().toLowerCase();
    } else {
      connectionStatus.value = 'close';
    }
  } catch (error) {
    connectionStatus.value = 'close';
  }
};

const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const waitForQrReady = async (attempts = 8, delayMs = 2000) => {
  const attemptFetch = async remainingAttempts => {
    if (remainingAttempts <= 0) {
      return false;
    }

    if (remainingAttempts < attempts) {
      await sleep(delayMs);
    }

    await loadConnectionStatus();
    const status = connectionStatus.value || '';

    if (qrScanStatuses.has(status)) {
      return true;
    }

    if (connectedStatuses.has(status)) {
      return false;
    }

    return attemptFetch(remainingAttempts - 1);
  };

  return attemptFetch(attempts);
};

const matchesInbox = payload => {
  const incoming = payload.inbox_id ?? payload.inboxId;
  if (incoming !== undefined && incoming !== null) {
    return String(incoming) === String(inbox.value.id);
  }

  const metaId = payload.metadata?.['chatwoot.inbox_id'];
  if (metaId !== undefined && metaId !== null) {
    return String(metaId) === String(inbox.value.id);
  }

  return false;
};

const onQrCodeUpdated = async payload => {
  if (!matchesInbox(payload)) return;
  qrCode.value = await normalizeQrPayload(payload.qr_code);
  showQrModal.value = Boolean(qrCode.value);
};

const onConnectionUpdate = payload => {
  if (!matchesInbox(payload)) return;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[WAHA] connection update', inbox.value.id, payload);
  }
  connectionStatus.value = (payload.status || '').toLowerCase();
  if (!qrScanStatuses.has(connectionStatus.value)) {
    qrCode.value = null;
    showQrModal.value = false;
    loadConnectionStatus();
  }
};

const closeQrModal = () => {
  showQrModal.value = false;
  loadConnectionStatus();
};

const refreshStatus = async () => {
  await loadConnectionStatus();
  useAlert(t('INBOX_MGMT.ADD.WAHA.STATUS.REFRESHED'));
};

const generateQRCode = async () => {
  try {
    if (!sessionName.value) {
      useAlert(t('INBOX_MGMT.ADD.WAHA.QR_CODE.NO_QR'));
      return;
    }

    isGeneratingQR.value = true;

    await loadConnectionStatus();
    const currentStatus = connectionStatus.value || '';
    if (currentStatus === 'failed') {
      try {
        await WahaAPI.restartSession(accountId.value, inbox.value.id);
      } catch (restartError) {
        useAlert(
          restartError.message || t('INBOX_MGMT.ADD.WAHA.RECREATE.ERROR')
        );
        isGeneratingQR.value = false;
        return;
      }

      const readyAfterRestart = await waitForQrReady();
      if (!readyAfterRestart) {
        useAlert(t('INBOX_MGMT.ADD.WAHA.QR_CODE.NO_QR'));
        isGeneratingQR.value = false;
        return;
      }
    }

    const payload = await WahaAPI.fetchQrCode(
      accountId.value,
      inbox.value.id
    );
    const qr = await normalizeQrPayload(payload);
    if (qr && qrScanStatuses.has(connectionStatus.value || '')) {
      qrCode.value = qr;
      showQrModal.value = true;
      useAlert(t('INBOX_MGMT.ADD.WAHA.QR_CODE.SUCCESS'));
    } else {
      useAlert(t('INBOX_MGMT.ADD.WAHA.QR_CODE.NO_QR'));
    }
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.QR_CODE.ERROR'));
  } finally {
    isGeneratingQR.value = false;
  }
};

const disconnectSession = async () => {
  try {
    if (!sessionName.value) {
      return;
    }

    isSessionAction.value = true;

    try {
      await WahaAPI.stopSession(accountId.value, inbox.value.id);
    } catch (stopError) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          '[WAHA] stop session failed, attempting delete',
          stopError
        );
      }
      await WahaAPI.deleteSession(accountId.value, inbox.value.id);
    }

    connectionStatus.value = 'close';
    qrCode.value = null;
    await loadConnectionStatus();
    useAlert(t('INBOX_MGMT.ADD.WAHA.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.DISCONNECT.ERROR'));
  } finally {
    isSessionAction.value = false;
  }
};

const startSession = async () => {
  try {
    if (!sessionName.value) {
      return;
    }

    isSessionAction.value = true;

    await WahaAPI.restartSession(accountId.value, inbox.value.id);

    await loadConnectionStatus();
    useAlert(t('INBOX_MGMT.ADD.WAHA.START.SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.START.ERROR'));
  } finally {
    isSessionAction.value = false;
  }
};

const recreateSession = async () => {
  try {
    isRecreating.value = true;
    try {
      if (accountId.value && inbox.value?.id) {
        await WahaAPI.logoutSession(accountId.value, inbox.value.id);
      }
    } catch (logoutError) {
      // No detengas todo el flujo si WAHA ya eliminó la sesión
      // eslint-disable-next-line no-console
      console.warn('[WAHA] Logout session failed', logoutError);
    }

    const response = await InboxesAPI.recreateWahaInstance(inbox.value.id, {
      force: true,
    });
    const qr = await normalizeQrPayload(response.data?.qrcode);
    if (qr && qrScanStatuses.has(connectionStatus.value || '')) {
      qrCode.value = qr;
      showQrModal.value = true;
    }
    await loadConnectionStatus();
    useAlert(t('INBOX_MGMT.ADD.WAHA.RECREATE.SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.RECREATE.ERROR'));
  } finally {
    isRecreating.value = false;
  }
};

const handleSessionAction = async () => {
  if (isSessionAction.value) {
    return;
  }

  if (isConnected.value) {
    await disconnectSession();
  } else {
    await startSession();
  }
};

// Load filter settings from provider_config
const loadFilterSettings = () => {
  const config = providerConfig.value;
  filterSettings.value = {
    ignoreGroups: config.ignore_groups ?? false,
    ignoreChannels: config.ignore_channels ?? false,
    ignoreStatus: config.ignore_status ?? false,
    ignoreBroadcast: config.ignore_broadcast ?? false,
  };
};

// Update filter settings
const updateFilterSettings = async () => {
  try {
    isUpdatingFilters.value = true;

    const payload = {
      id: inbox.value.id,
      formData: false,
      channel: {
        provider_config: {
          ...providerConfig.value,
          ignore_groups: filterSettings.value.ignoreGroups,
          ignore_channels: filterSettings.value.ignoreChannels,
          ignore_status: filterSettings.value.ignoreStatus,
          ignore_broadcast: filterSettings.value.ignoreBroadcast,
        },
      },
    };

    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('INBOX_MGMT.ADD.WAHA.FILTERS.SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.FILTERS.ERROR'));
  } finally {
    isUpdatingFilters.value = false;
  }
};

onMounted(() => {
  loadConnectionStatus();
  loadFilterSettings();
  emitter.on(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, onQrCodeUpdated);
  emitter.on(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);
});

watch(connectionStatus, status => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[WAHA] connection status changed', inbox.value.id, status);
  }
  if (!qrScanStatuses.has(status)) {
    qrCode.value = null;
    showQrModal.value = false;
  }
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, onQrCodeUpdated);
  emitter.off(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.WAHA.MANAGEMENT.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.WAHA.MANAGEMENT.SUBTITLE')"
    >
      <div class="flex flex-col gap-4">
        <InboxName
          :inbox="inbox"
          class="!text-lg !m-0"
          with-phone-number
          with-provider-connection-status
        />

        <div class="flex items-center gap-2">
          <span class="font-medium">
            {{ $t('INBOX_MGMT.ADD.WAHA.STATUS.LABEL') }}
          </span>
          <span
            class="font-semibold"
            :class="{
              'text-green-600': isStatusOpen(connectionStatus),
              'text-yellow-600': ['connecting', 'starting'].includes(
                connectionStatus
              ),
              'text-red-600': ['close', 'stopped', 'failed'].includes(
                connectionStatus
              ),
              'text-gray-600': !connectionStatus,
            }"
          >
            {{ connectionStatusLabel }}
          </span>
          <NextButton
            size="small"
            variant="link"
            :label="$t('INBOX_MGMT.ADD.WAHA.STATUS.REFRESH')"
            @click="refreshStatus"
          />
        </div>

        <div class="flex flex-wrap gap-2">
          <NextButton
            v-if="canShowQRCode"
            :label="$t('INBOX_MGMT.ADD.WAHA.QR_CODE.GENERATE')"
            :is-loading="isGeneratingQR"
            @click="generateQRCode"
          />

          <NextButton
            class="bg-woot-500 hover:bg-woot-600 text-white"
            :label="$t('INBOX_MGMT.ADD.WAHA.RECREATE.BUTTON')"
            :is-loading="isRecreating"
            @click="recreateSession"
          />

          <NextButton
            :label="actionButtonLabel"
            :is-loading="isSessionAction"
            :class="primaryActionClasses"
            @click="handleSessionAction"
          />
        </div>
      </div>
    </SettingsSection>

    <!-- Message Filters Section -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.WAHA.FILTERS.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.WAHA.FILTERS.SUBTITLE')"
    >
      <div class="space-y-4">
        <!-- Ignore Groups -->
        <div class="flex items-center gap-2">
          <input
            id="ignoreGroups"
            v-model="filterSettings.ignoreGroups"
            type="checkbox"
          />
          <label for="ignoreGroups" class="text-sm font-medium">
            {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.GROUPS_LABEL') }}
          </label>
        </div>
        <p class="text-xs text-slate-500 ml-6 -mt-2">
          {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.GROUPS_DESCRIPTION') }}
        </p>

        <!-- Ignore Channels -->
        <div class="flex items-center gap-2">
          <input
            id="ignoreChannels"
            v-model="filterSettings.ignoreChannels"
            type="checkbox"
          />
          <label for="ignoreChannels" class="text-sm font-medium">
            {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.CHANNELS_LABEL') }}
          </label>
        </div>
        <p class="text-xs text-slate-500 ml-6 -mt-2">
          {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.CHANNELS_DESCRIPTION') }}
        </p>

        <!-- Ignore Status -->
        <div class="flex items-center gap-2">
          <input
            id="ignoreStatus"
            v-model="filterSettings.ignoreStatus"
            type="checkbox"
          />
          <label for="ignoreStatus" class="text-sm font-medium">
            {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.STATUS_LABEL') }}
          </label>
        </div>
        <p class="text-xs text-slate-500 ml-6 -mt-2">
          {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.STATUS_DESCRIPTION') }}
        </p>

        <!-- Ignore Broadcast -->
        <div class="flex items-center gap-2">
          <input
            id="ignoreBroadcast"
            v-model="filterSettings.ignoreBroadcast"
            type="checkbox"
          />
          <label for="ignoreBroadcast" class="text-sm font-medium">
            {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.BROADCAST_LABEL') }}
          </label>
        </div>
        <p class="text-xs text-slate-500 ml-6 -mt-2">
          {{ $t('INBOX_MGMT.ADD.WAHA.FILTERS.BROADCAST_DESCRIPTION') }}
        </p>

        <NextButton
          :label="$t('INBOX_MGMT.ADD.WAHA.FILTERS.UPDATE')"
          :is-loading="isUpdatingFilters"
          @click="updateFilterSettings"
        />
      </div>
    </SettingsSection>

    <Modal v-model:show="showQrModal" :on-close="closeQrModal">
      <div class="flex flex-col items-center gap-4 p-6">
        <h3 class="text-lg font-semibold">
          {{ $t('INBOX_MGMT.ADD.WAHA.QR_CODE.TITLE') }}
        </h3>
        <p class="text-sm text-gray-600 text-center">
          {{ $t('INBOX_MGMT.ADD.WAHA.QR_CODE.INSTRUCTIONS') }}
        </p>
        <img v-if="qrCode" :src="qrCode" alt="WhatsApp QR" class="w-64 h-64" />
      </div>
    </Modal>
  </div>
</template>
